import { useMemo, useState, type ReactNode } from "react";
import { skipToken } from "@tanstack/react-query";
import { DateTime } from "luxon";
import {
  quoteTrackingLabels,
  type ExistingPatient,
  type QuoteRealization,
} from "cadendar-shared";
import { trpc } from "../../main/components/trpc";
import { usePatientQuotes } from "../../quotes/useQuotes";
import { usePatientConsultations } from "../hooks/usePatientConsultations";
import {
  getActeLabel,
  getClinicalSummaryEntries,
  getLastClinicalSession,
  getLatestConsultation,
  getPatientDate,
  groupOverviewQuotes,
  type PatientOverviewQuote,
} from "../patientOverview";
import PecCoverageStatus from "./PecCoverageStatus";
import { pecMap, stateMap } from "./patientStateMaps";

interface PatientOverviewProps {
  patient: ExistingPatient;
  canReadConsultations: boolean;
  onOpenHistory: (date?: string) => void;
  onOpenConsultations: () => void;
  onOpenImaging: () => void;
  onOpenQuote: (quoteId?: string) => void;
  onOpenPec: (documentId?: string) => void;
}

function OverviewCard({
  title,
  children,
  action,
  className,
}: {
  title: string;
  children: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`min-w-0 rounded-xl border border-slate-200 bg-white p-5 shadow-sm ${className ?? ""}`}
    >
      <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function SourceError({
  children,
  onRetry,
}: {
  children: ReactNode;
  onRetry: () => void;
}) {
  return (
    <p role="alert" className="text-sm text-red-700">
      {children}{" "}
      <button type="button" className="underline" onClick={onRetry}>
        Réessayer
      </button>
    </p>
  );
}

const actionClass = "text-xs font-medium text-blue-700 hover:underline";
const realizationLabels: Record<QuoteRealization["state"], string> = {
  none: "Aucun acte retrouvé",
  partial: "Partiellement réalisé",
  performed: "Actes retrouvés",
  ambiguous: "Correspondances ambiguës",
  unverifiable: "Réalisation non vérifiable",
  excluded: "Détection exclue",
};

export default function PatientOverview({
  patient,
  canReadConsultations,
  ...actions
}: PatientOverviewProps) {
  const actesQuery = trpc.logos.getActesByPatientLogosNumber.useQuery(
    patient.logos_file_number ?? skipToken,
  );
  const quotesQuery = usePatientQuotes(patient.id, { refetchInterval: 30_000 });
  const pecQuery = trpc.pec.listPecDocuments.useQuery(
    { patientId: patient.id },
    {
      refetchInterval: (query) =>
        query.state.data?.some((document) =>
          ["pending", "processing"].includes(document.ocrStatus),
        )
          ? 5_000
          : false,
    },
  );
  const statesQuery = trpc.patient.getStatesByIds.useQuery([patient.id]);
  const consultationsQuery = usePatientConsultations(
    patient.id,
    canReadConsultations,
  );
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [showAllAccepted, setShowAllAccepted] = useState(false);
  const [showProposals, setShowProposals] = useState(false);
  const session = useMemo(
    () =>
      getLastClinicalSession(actesQuery.error ? [] : (actesQuery.data ?? [])),
    [actesQuery.data, actesQuery.error],
  );
  const entries = useMemo(
    () =>
      getClinicalSummaryEntries(
        actesQuery.error ? [] : (actesQuery.data ?? []),
      ),
    [actesQuery.data, actesQuery.error],
  );
  const consultation = useMemo(
    () =>
      canReadConsultations && !consultationsQuery.error
        ? getLatestConsultation(consultationsQuery.data ?? [])
        : null,
    [canReadConsultations, consultationsQuery.data, consultationsQuery.error],
  );
  const quotes = useMemo(
    () =>
      groupOverviewQuotes(quotesQuery.error ? [] : (quotesQuery.data ?? [])),
    [quotesQuery.data, quotesQuery.error],
  );
  const patientState = statesQuery.error
    ? undefined
    : statesQuery.data?.find((state) => state.patient_id === patient.id);
  const unassignedDocuments =
    pecQuery.data?.filter((document) => !document.quoteId) ?? [];

  function renderQuote(quote: PatientOverviewQuote) {
    const teeth = [
      ...new Set(
        quote.lines
          .filter((line) => !line.is_alternative)
          .flatMap((line) => line.teeth),
      ),
    ];
    return (
      <article
        key={quote.id}
        className="rounded-lg border border-slate-200 p-3"
      >
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <button
              type="button"
              className="break-words text-left text-sm font-semibold text-blue-800 hover:underline"
              onClick={() => actions.onOpenQuote(quote.id)}
            >
              {quote.name || "Devis sans titre"}
            </button>
            <p className="mt-1 text-xs text-slate-500">
              {DateTime.fromJSDate(quote.creation_date).toFormat("dd/MM/yyyy")}
              {teeth.length ? ` · Dents ${teeth.join(", ")}` : ""}
            </p>
          </div>
          <span className="rounded bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700">
            Devis{" "}
            {quoteTrackingLabels[quote.tracking.state].toLocaleLowerCase("fr")}
          </span>
        </div>
        <p className="mt-2 text-xs text-slate-600">
          {quote.realization
            ? realizationLabels[quote.realization.state]
            : "Réalisation non renseignée"}
          {quote.realization && quote.realization.totalLines > 0
            ? ` · ${quote.realization.performedLines}/${quote.realization.totalLines} actes retrouvés`
            : ""}
        </p>
        <div className="mt-3">
          {pecQuery.error ? (
            <p className="text-xs text-red-700">Suivi PEC indisponible.</p>
          ) : pecQuery.isPending ? (
            <p className="text-xs text-slate-500">Chargement des PEC…</p>
          ) : (
            <PecCoverageStatus
              documents={pecQuery.data ?? []}
              quoteId={quote.id}
              onOpenDocument={actions.onOpenPec}
            />
          )}
        </div>
        <button
          type="button"
          className={`${actionClass} mt-3`}
          onClick={() => actions.onOpenQuote(quote.id)}
        >
          Vérifier la réalisation et la prochaine étape
        </button>
      </article>
    );
  }

  return (
    <div className="grid items-start gap-4 xl:grid-cols-2">
      <OverviewCard
        title="Dernière séance"
        action={
          <button
            type="button"
            className={actionClass}
            onClick={() => actions.onOpenHistory()}
          >
            Historique complet →
          </button>
        }
      >
        {patient.logos_file_number !== null && actesQuery.error ? (
          <SourceError onRetry={() => void actesQuery.refetch()}>
            Historique clinique indisponible.
          </SourceError>
        ) : patient.logos_file_number !== null && actesQuery.isPending ? (
          <p className="text-sm text-slate-500">Chargement des soins…</p>
        ) : session ? (
          <div>
            <p className="text-xl font-semibold text-slate-900">
              {session.date
                ? getPatientDate(session.date).toFormat("dd/MM/yyyy")
                : "Date inconnue"}
            </p>
            <ul className="mt-4 space-y-3">
              {session.actes.map((acte) => (
                <li
                  key={`${acte.page_index}-${acte.timestamp}`}
                  className="text-sm text-slate-800"
                >
                  <span className="font-medium">{getActeLabel(acte)}</span>
                  {acte.teeth ? (
                    <span className="text-slate-500">
                      {" "}
                      · Dents {acte.teeth}
                    </span>
                  ) : null}
                  {acte.comment ? (
                    <p className="mt-1 whitespace-pre-wrap text-slate-600">
                      {acte.comment}
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
            {session.notes.length ? (
              <details className="mt-3 text-sm text-slate-600">
                <summary className="cursor-pointer">
                  Notes du même jour
                </summary>
                {session.notes.map((note) => (
                  <p
                    key={`${note.page_index}-${note.timestamp}`}
                    className="mt-2 whitespace-pre-wrap"
                  >
                    {[note.description, note.comment]
                      .filter(Boolean)
                      .join("\n")}
                  </p>
                ))}
              </details>
            ) : null}
            <div className="mt-4 flex flex-wrap gap-4">
              <button
                type="button"
                className={actionClass}
                onClick={() => actions.onOpenHistory(session.date ?? undefined)}
              >
                Voir les actes sources →
              </button>
              <button
                type="button"
                className={actionClass}
                onClick={actions.onOpenImaging}
              >
                Ouvrir l’imagerie →
              </button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-500">
            {patient.logos_file_number === null
              ? "Aucun historique LOGOS rattaché."
              : "Aucun acte clinique daté retrouvé."}
          </p>
        )}

        {canReadConsultations ? (
          <div className="mt-4 border-t border-slate-100 pt-4">
            {consultationsQuery.error ? (
              <SourceError onRetry={() => void consultationsQuery.refetch()}>
                Comptes-rendus indisponibles.
              </SourceError>
            ) : consultationsQuery.isPending ? (
              <p className="text-xs text-slate-500">
                Chargement des comptes-rendus…
              </p>
            ) : consultation ? (
              <div>
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="font-medium text-slate-700">
                    Dernier compte-rendu ·{" "}
                    {getPatientDate(consultation.recordingStartedAt).toFormat(
                      "dd/MM/yyyy",
                    )}
                  </span>
                  <span
                    className={
                      consultation.status === "validated"
                        ? "text-emerald-800"
                        : "font-semibold text-amber-800"
                    }
                  >
                    {consultation.status === "validated"
                      ? "Validé"
                      : "Brouillon non validé"}
                  </span>
                </div>
                <p className="mt-2 line-clamp-4 whitespace-pre-wrap text-sm text-slate-700">
                  {consultation.status === "validated"
                    ? consultation.validatedReport
                    : consultation.draftReport}
                </p>
              </div>
            ) : null}
            <button
              type="button"
              className={`${actionClass} mt-3`}
              onClick={actions.onOpenConsultations}
            >
              Consulter ou enregistrer une consultation →
            </button>
          </div>
        ) : null}
      </OverviewCard>

      <OverviewCard
        title="Suite du traitement"
        className="xl:col-start-2 xl:row-start-1 xl:row-span-2"
        action={
          <button
            type="button"
            className={actionClass}
            onClick={() => actions.onOpenQuote()}
          >
            Tous les devis →
          </button>
        }
      >
        {quotesQuery.error ? (
          <SourceError onRetry={() => void quotesQuery.refetch()}>
            Devis indisponibles.
          </SourceError>
        ) : quotesQuery.isPending ? (
          <p className="text-sm text-slate-500">Chargement des devis…</p>
        ) : (
          <>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Acceptés · réalisation à suivre ({quotes.accepted.length})
            </h3>
            <div className="space-y-3">
              {quotes.accepted.length ? (
                (showAllAccepted
                  ? quotes.accepted
                  : quotes.accepted.slice(0, 2)
                ).map(renderQuote)
              ) : (
                <p className="text-sm text-slate-500">
                  Aucun devis accepté à suivre.
                </p>
              )}
            </div>
            {quotes.accepted.length > 2 ? (
              <button
                type="button"
                className={`${actionClass} mt-3`}
                onClick={() => setShowAllAccepted(!showAllAccepted)}
              >
                {showAllAccepted
                  ? "Réduire"
                  : `Voir les ${quotes.accepted.length} projets acceptés`}
              </button>
            ) : null}
            {quotes.proposed.length ? (
              <div className="mt-4 border-t border-slate-100 pt-3">
                <button
                  type="button"
                  className="text-sm font-medium text-slate-700"
                  aria-expanded={showProposals}
                  onClick={() => setShowProposals(!showProposals)}
                >
                  Propositions à décider ({quotes.proposed.length}){" "}
                  {showProposals ? "−" : "+"}
                </button>
                {showProposals ? (
                  <div className="mt-3 space-y-3">
                    {quotes.proposed.map(renderQuote)}
                  </div>
                ) : null}
              </div>
            ) : null}
            {quotes.archived.length ? (
              <p className="mt-3 text-xs text-slate-500">
                {quotes.archived.length} devis clôturé(s) dans la vue détaillée.
              </p>
            ) : null}
          </>
        )}
        <div className="mt-4 border-t border-slate-100 pt-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Suivi global du patient
          </h3>
          {statesQuery.error ? (
            <SourceError onRetry={() => void statesQuery.refetch()}>
              Suivi global indisponible.
            </SourceError>
          ) : statesQuery.isPending ? (
            <p className="mt-2 text-xs text-slate-500">Chargement du suivi…</p>
          ) : (
            <>
              <p className="mt-2 text-sm text-slate-700">
                PEC :{" "}
                {patientState?.pec_state
                  ? pecMap[patientState.pec_state]
                  : "non renseignée"}
              </p>
              {patientState?.state ? (
                <p className="mt-1 text-xs text-slate-600">
                  {stateMap[patientState.state]}
                </p>
              ) : null}
              {patientState?.comment ? (
                <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">
                  {patientState.comment}
                </p>
              ) : null}
            </>
          )}
          {pecQuery.error ? (
            <div className="mt-2">
              <SourceError onRetry={() => void pecQuery.refetch()}>
                Documents PEC indisponibles.
              </SourceError>
            </div>
          ) : !pecQuery.isPending && unassignedDocuments.length ? (
            <button
              type="button"
              className={`${actionClass} mt-3`}
              onClick={() => actions.onOpenPec(unassignedDocuments[0]?.id)}
            >
              {unassignedDocuments.length} document(s) PEC sans devis lié · à
              classer →
            </button>
          ) : null}
        </div>
      </OverviewCard>
      <OverviewCard
        title="Grandes lignes de l’historique"
        action={
          <button
            type="button"
            className={actionClass}
            onClick={() => actions.onOpenHistory()}
          >
            Tout l’historique →
          </button>
        }
      >
        {patient.logos_file_number !== null && actesQuery.error ? (
          <SourceError onRetry={() => void actesQuery.refetch()}>
            Historique indisponible.
          </SourceError>
        ) : patient.logos_file_number !== null && actesQuery.isPending ? (
          <p className="text-sm text-slate-500">Chargement de l’historique…</p>
        ) : entries.length ? (
          <>
            <ol className="grid gap-x-8 gap-y-3 md:grid-cols-2">
              {(showAllHistory ? entries : entries.slice(0, 6)).map((entry) => (
                <li
                  key={entry.key}
                  className="border-l-2 border-slate-200 pl-3"
                >
                  <p className="text-xs text-slate-500">
                    {entry.year} · {entry.month}
                  </p>
                  <p className="mt-1 text-sm font-medium text-slate-800">
                    {entry.shortLabel}
                    {entry.teeth.length ? (
                      <span className="font-normal text-slate-500">
                        {" "}
                        · {entry.teeth.join(", ")}
                      </span>
                    ) : null}
                  </p>
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
                    {entry.dates.length ? (
                      entry.dates.map((date) => (
                        <button
                          key={date}
                          type="button"
                          className={actionClass}
                          onClick={() => actions.onOpenHistory(date)}
                        >
                          Actes du {getPatientDate(date).toFormat("dd/MM/yyyy")}{" "}
                          →
                        </button>
                      ))
                    ) : (
                      <button
                        type="button"
                        className={actionClass}
                        onClick={() => actions.onOpenHistory()}
                      >
                        Voir la source →
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ol>
            {entries.length > 6 ? (
              <button
                type="button"
                className={`${actionClass} mt-4`}
                onClick={() => setShowAllHistory(!showAllHistory)}
              >
                {showAllHistory
                  ? "Réduire l’historique"
                  : `Afficher les ${entries.length - 6} autres éléments marquants`}
              </button>
            ) : null}
          </>
        ) : (
          <p className="text-sm text-slate-500">
            {patient.logos_file_number === null
              ? "Aucun historique LOGOS rattaché."
              : "Aucun acte marquant identifié."}
          </p>
        )}
      </OverviewCard>
    </div>
  );
}
